import React, { createContext, useContext, useState, useEffect } from "react";

const translations = {
  nav: {
    about: { fr: "Approche", en: "Approach" },
    services: { fr: "Services", en: "Services" },
    shop: { fr: "Boutique", en: "Shop" },
    booking: { fr: "Réserver", en: "Book" },
    blog: { fr: "Ressources", en: "Resources" },
    members: { fr: "Abonnement", en: "Subscription" },
    login: { fr: "Connexion", en: "Log in" },
    logout: { fr: "Déconnexion", en: "Log out" },
    contact: { fr: "Contact", en: "Contact" },
  },
  hero: {
    eyebrow: { fr: "Vitalité nordique", en: "Nordic vitality" },
    l1: { fr: "Nourrir", en: "Nourish" },
    l2: { fr: "l'esprit,", en: "the mind," },
    l3: { fr: "restaurer", en: "restore" },
    l4: { fr: "la vitalité.", en: "vitality." },
    sub: {
      fr: "Accompagnatrice Pair certifiée CPMC et CPMC-T (formatrice), j'intègre le lien entre alimentation et santé mentale à ma pratique, dans une approche axée sur le rétablissement. J'accompagne chaque personne dans la découverte de sa médecine personnelle : ces activités et habitudes qui donnent du sens à sa vie et soutiennent son bien-être. Formée auprès de Pat Deegan et associés, ainsi qu'auprès de Dre Uma Naidoo, psychiatre spécialisée en nutrition à Harvard.",
      en: "Certified peer supporter (CPMC and CPMC-T trainer), I bring the connection between food and mental health into my practice, with a recovery-oriented approach. I walk with each person as they discover their personal medicine: the activities and habits that give meaning to their life and support their well-being. Trained with Pat Deegan & associates, and with Dr. Uma Naidoo, Harvard nutrition psychiatrist.",
    },
    cta1: { fr: "Réserver un appel", en: "Book a call" },
    cta2: { fr: "Découvrir l'approche", en: "Explore the approach" },
  },
  marquee: {
    items: {
      fr: ["Partir de vos forces", "Lien assiette-esprit", "Bouger, respirer, se reconnecter", "De pair à pair, sans jugement"],
      en: ["Rooted in your strengths", "Plate-mind connection", "Move, breathe, reconnect", "Peer to peer, without judgment"],
    },
  },
  manifesto: {
    title: { fr: "4 ancrages", en: "4 anchors" },
    chapters: {
      fr: [
        { t: "Un rétablissement qui part de vos forces", d: "Ce que vous avez traversé n'est pas qu'un diagnostic, c'est un savoir. Au-delà des traitements, il y a votre médecine personnelle : ces activités, ces liens, ces passions qui vous font vous lever le matin. On part de vos forces, à votre rythme, un choix à la fois." },
        { t: "Votre assiette fait partie de votre rétablissement", d: "L'intestin et le cerveau dialoguent sans cesse, et la recherche en psychiatrie nutritionnelle le montre de plus en plus. Pas de régime ni de culpabilité, seulement des choix qui nourrissent l'humeur." },
        { t: "Bouger, respirer, se reconnecter", d: "Marcher avec son pitou, poser les mains sur une écorce, plonger les pieds dans l'herbe — le corps en mouvement et le temps passé dehors apaisent le système nerveux et nourrissent l'équilibre. Le mouvement et le vivant font partie du chemin." },
        { t: "Côte à côte, sans jugement", d: "J'ai fait ce chemin moi aussi. Je ne vous parle pas d'en haut, je marche à vos côtés. Chaque accompagnement commence par la sécurité et le choix. Dans l'accueil, sans jugement : là où le trauma a imposé, l'accompagnement propose." },
      ],
      en: [
        { t: "A recovery rooted in your strengths", d: "What you've been through isn't just a diagnosis, it's knowledge. Beyond treatment, there's your personal medicine: the activities, connections and passions that make you want to get up in the morning. We start from your strengths, at your pace, one choice at a time." },
        { t: "What's on your plate is part of your recovery", d: "The gut and the brain are in constant conversation, and research in nutritional psychiatry increasingly shows it. No diets, no guilt, just choices that nourish your mood." },
        { t: "Move, breathe, reconnect", d: "Walking with your pup, resting a hand on bark, sinking your feet in the grass — a body in motion and time spent outside calm the nervous system and nourish balance. Movement and the living world are part of the journey." },
        { t: "Side by side, without judgment", d: "I've walked this path too. I don't speak to you from above, I walk beside you. Every session begins with safety and choice. In warmth, without judgment: where trauma imposed, support offers." },
      ],
    },
  },
  services: {
    eyebrow: { fr: "Par des accompagnateurs pairs CPMC", en: "By CPMC peer supporters" },
    title: { fr: "Comment nous pouvons cheminer ensemble", en: "How we can walk together" },
    items: {
      fr: [
        { t: "Se rétablir du trauma complexe, à votre façon.", d: "Le trauma complexe (TSPT-C) laisse des traces, et la stigmatisation en ajoute. Comme pair-aidante, je marche à vos côtés pour vous aider à retrouver ce qui vous fait du bien, jour après jour, jusque dans l'assiette." },
        { t: "Nourrir l'esprit, soutenir le rétablissement", d: "Le regard de l'intérieur — des outils concrets et des repères pratiques pour favoriser un esprit plus clair et un corps plus vital, inspirés du vécu d'une pair aidante formée en nutrition et santé mentale." },
        { t: "Mouvement et nature pour briser l'isolement", d: "Se reconnecter à soi, aux autres et au vivant — bouger, respirer, marcher, ensemble." },
      ],
      en: [
        { t: "Healing from complex trauma, at your pace.", d: "Complex trauma (C-PTSD) leaves its mark, and stigma adds to it. As a peer supporter, I walk beside you to help you rediscover what makes you feel good, day by day, right down to your plate." },
        { t: "Nourishing the mind, supporting recovery", d: "An inside view — practical tools and grounded guidance to foster a clearer mind and a more vital body, drawn from the lived experience of a peer supporter trained in nutrition and mental health." },
        { t: "Movement & nature to break isolation", d: "Reconnect with yourself, others and the living world — move, breathe, walk, together." },
      ],
    },
  },
  recipe: {
    eyebrow: { fr: "Générateur IA", en: "AI Generator" },
    title: { fr: "Une recette pour votre humeur", en: "A recipe for your mood" },
    sub: { fr: "Dites-moi ce que vous avez sous la main, et je compose une recette mood-food, pensée pour votre esprit.", en: "Tell me what you have on hand, and I'll compose a mood-food recipe designed for your mind." },
    ingredients: { fr: "Ingrédients disponibles", en: "Available ingredients" },
    ingredientsPh: { fr: "épinards, saumon, quinoa…", en: "spinach, salmon, quinoa…" },
    mood: { fr: "Objectif / humeur", en: "Goal / mood" },
    dietary: { fr: "Régime (optionnel)", en: "Dietary (optional)" },
    generate: { fr: "Composer ma recette", en: "Compose my recipe" },
    generating: { fr: "Composition en cours…", en: "Composing…" },
    benefit: { fr: "Bienfait pour l'humeur", en: "Mood benefit" },
    steps: { fr: "Étapes", en: "Steps" },
    tip: { fr: "Astuce", en: "Tip" },
    moods: {
      fr: ["Calme", "Énergie", "Focus", "Sommeil", "Réconfort"],
      en: ["Calm", "Energy", "Focus", "Sleep", "Comfort"],
    },
    paywallTitle: { fr: "Essayer à partir de 4,99 $ par mois", en: "Try it from $4.99 per month" },
    paywallSub: { fr: "Débloquez le générateur de recettes IA avec le forfait Basic : 10 recettes IA/mois, infolettre hebdomadaire et revue mensuelle en PDF.", en: "Unlock the AI recipe generator with the Basic plan: 10 AI recipes/month, weekly newsletter and monthly PDF magazine." },
    subscribe: { fr: "S'abonner", en: "Subscribe" },
    loginToSub: { fr: "Se connecter pour s'abonner", en: "Log in to subscribe" },
    popular: { fr: "Recommandé", en: "Recommended" },
    perWeek: { fr: "/ sem", en: "/ wk" },
    freeLeft: { fr: "", en: "" },
    unlimitedBadge: { fr: "Accès au générateur ✦", en: "Generator access ✦" },
  },
  shop: {
    title: { fr: "Livres, ebooks & programmes", en: "Books, ebooks & programs" },
    sub: { fr: "Des ressources créées avec soin pour poursuivre le chemin chez vous.", en: "Resources crafted with care to continue the journey at home." },
    buy: { fr: "Acheter", en: "Buy" },
    comingSoon: { fr: "Bientôt disponible", en: "Coming soon" },
    subscribe: { fr: "S'abonner", en: "Subscribe" },
    viewAll: { fr: "Voir toute la boutique", en: "View full shop" },
    perMonth: { fr: "/ mois", en: "/ mo" },
    perYear: { fr: "/ an", en: "/ yr" },
    categories: {
      fr: { physical: "Livre", ebook: "Ebook", pamphlet: "Pamphlets", program: "Programme", session: "Séance", membership: "Abonnement" },
      en: { physical: "Book", ebook: "Ebook", pamphlet: "Pamphlets", program: "Program", session: "Session", membership: "Membership" },
    },
  },
  booking: {
    title: { fr: "Réserver une séance", en: "Book a session" },
    sub: { fr: "Choisissez un type de séance, une date et un créneau. Vous recevrez une confirmation par email.", en: "Choose a session type, a date and a slot. You'll receive an email confirmation." },
    type: { fr: "Type de séance", en: "Session type" },
    date: { fr: "Date", en: "Date" },
    slots: { fr: "Créneaux disponibles", en: "Available slots" },
    name: { fr: "Nom complet", en: "Full name" },
    email: { fr: "Email", en: "Email" },
    notes: { fr: "Notes (optionnel)", en: "Notes (optional)" },
    confirm: { fr: "Confirmer la réservation", en: "Confirm booking" },
    pickDate: { fr: "Sélectionnez une date pour voir les créneaux.", en: "Select a date to see available slots." },
    noSlots: { fr: "Aucun créneau ce jour-là.", en: "No slots that day." },
    success: { fr: "Réservation confirmée ! Vérifiez votre boîte mail.", en: "Booking confirmed! Check your inbox." },
    redirect: { fr: "Redirection vers le paiement sécurisé…", en: "Redirecting to secure checkout…" },
    types: {
      fr: [
        { key: "discovery", label: "Appel découverte — gratuit (20 min)" },
        { key: "session_60", label: "Séance Soutien & Nutrition — 120 $ (60 min)" },
      ],
      en: [
        { key: "discovery", label: "Discovery call — free (20 min)" },
        { key: "session_60", label: "Support & Nutrition session — $120 (60 min)" },
      ],
    },
  },
  members: {
    title: { fr: "Abonnement Nordicvy", en: "Nordicvy Subscription" },
    sub: { fr: "Rejoignez un espace chaleureux de rétablissement et de reconnexion : recettes exclusives, revue mensuelle en PDF et ressources réservées aux abonnés.", en: "Join a warm space of recovery and reconnection: exclusive recipes, monthly PDF magazine and subscriber-only resources." },
    join: { fr: "S'abonner", en: "Subscribe" },
    monthly: { fr: "Mensuel", en: "Monthly" },
    yearly: { fr: "Annuel", en: "Yearly" },
    thisMonth: { fr: "Au programme ce mois-ci", en: "This month's drop" },
    locked: { fr: "Contenu réservé aux membres", en: "Member-only content" },
    unlock: { fr: "Débloquez avec l'abonnement mensuel.", en: "Unlock with the monthly membership." },
    welcome: { fr: "Bienvenue à l'Abonnement Nordicvy", en: "Welcome to Nordicvy Subscription" },
    loginToJoin: { fr: "Connectez-vous pour vous abonner.", en: "Log in to subscribe." },
  },
  blog: {
    title: { fr: "Ressources & réflexions", en: "Resources & reflections" },
    sub: { fr: "Nutrition, santé mentale et rétablissement — expliqués en douceur.", en: "Nutrition, mental health and recovery — gently explained." },
    read: { fr: "Lire", en: "Read" },
    back: { fr: "Retour aux ressources", en: "Back to resources" },
  },
  contact: {
    title: { fr: "Prenons contact", en: "Let's talk" },
    sub: { fr: "Une question, une collaboration, un simple bonjour ? Écrivez-moi.", en: "A question, a collaboration, a simple hello? Write to me." },
    name: { fr: "Votre nom", en: "Your name" },
    email: { fr: "Votre email", en: "Your email" },
    message: { fr: "Votre message", en: "Your message" },
    send: { fr: "Envoyer", en: "Send" },
    sent: { fr: "Message envoyé, merci !", en: "Message sent, thank you!" },
  },
  auth: {
    loginTitle: { fr: "Connexion", en: "Log in" },
    registerTitle: { fr: "Créer un compte", en: "Create account" },
    name: { fr: "Nom", en: "Name" },
    email: { fr: "Email", en: "Email" },
    password: { fr: "Mot de passe", en: "Password" },
    loginBtn: { fr: "Se connecter", en: "Log in" },
    registerBtn: { fr: "S'inscrire", en: "Sign up" },
    toRegister: { fr: "Pas encore de compte ? S'inscrire", en: "No account yet? Sign up" },
    toLogin: { fr: "Déjà un compte ? Se connecter", en: "Already have an account? Log in" },
  },
  chatbot: {
    title: { fr: "Compagnon Nordicvy", en: "Nordicvy Companion" },
    ph: { fr: "Posez une question bien-être…", en: "Ask a wellness question…" },
    greeting: { fr: "Bonjour 🌿 Je suis votre compagnon bien-être Nordicvy. Comment puis-je vous accompagner aujourd'hui ?", en: "Hello 🌿 I'm your Nordicvy wellness companion. How can I support you today?" },
    disclaimer: { fr: "Ceci ne remplace pas un avis médical.", en: "This does not replace medical advice." },
  },
  payment: {
    successTitle: { fr: "Paiement confirmé", en: "Payment confirmed" },
    successSub: { fr: "Merci ! Votre commande est confirmée. Un email de confirmation vous a été envoyé.", en: "Thank you! Your order is confirmed. A confirmation email is on its way." },
    checking: { fr: "Vérification du paiement…", en: "Verifying payment…" },
    cancelTitle: { fr: "Paiement annulé", en: "Payment cancelled" },
    cancelSub: { fr: "Aucun montant n'a été prélevé. Vous pouvez réessayer quand vous le souhaitez.", en: "No charge was made. You can try again whenever you like." },
    home: { fr: "Retour à l'accueil", en: "Back home" },
    members: { fr: "Accéder à mon abonnement", en: "Go to my subscription" },
  },
  footer: {
    cta: { fr: "Prenons soin de vous.", en: "Let's take care of you." },
    tagline: { fr: "Nourrir l'esprit, restaurer la vitalité.", en: "Nourish the mind, restore vitality." },
    rights: { fr: "Tous droits réservés.", en: "All rights reserved." },
  },
  social: {
    eyebrow: { fr: "Communauté", en: "Community" },
    title: { fr: "Rejoignez le mouvement", en: "Join the movement" },
    sub: { fr: "Recettes, coulisses et inspiration mood-food au quotidien. Suivez-nous et partagez le chemin.", en: "Recipes, behind-the-scenes and daily mood-food inspiration. Follow along and share the journey." },
    follow: { fr: "Suivre", en: "Follow" },
    shareBook: { fr: "Partager le livre", en: "Share the book" },
    shareBookSub: { fr: "Vous aimez « Nourrir » ? Faites-le découvrir autour de vous.", en: "Love “Nourish”? Help someone discover it." },
  },
  common: {
    from: { fr: "à partir de", en: "from" },
  },
};

const LanguageContext = createContext();

export const LanguageProvider = ({ children }) => {
  const [lang, setLang] = useState(() => localStorage.getItem("sereva_lang") || "fr");
  useEffect(() => {
    localStorage.setItem("sereva_lang", lang);
    document.documentElement.lang = lang;
  }, [lang]);

  const t = (path) => {
    const keys = path.split(".");
    let node = translations;
    for (const k of keys) {
      if (node == null) return path;
      node = node[k];
    }
    if (node && typeof node === "object" && (node.fr || node.en)) return node[lang];
    return node;
  };

  return (
    <LanguageContext.Provider value={{ lang, setLang, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLang = () => useContext(LanguageContext);
